/**
 * iframe Bridge — Spine app ile Rule Engine micro app arasındaki köprü.
 *
 * Rule Engine iframe içinde çalışır. Spine app (Zuzzuu_2.0 ana app):
 *   1. iframe'i oluşturur
 *   2. "auth:token" postMessage ile JWE token'ı gönderir
 *   3. Opsiyonel olarak "theme" / "locale" bilgilerini push edebilir
 *   4. "navigate" mesajı ile iframe içindeki router'ı yönlendirebilir
 *
 * Rule Engine iframe → Parent:
 *   - "ready" — iframe yüklendi, token gönderebilirsin
 *   - "height" — iframe yüksekliği değişti (auto-resize için)
 *   - "navigate" — iframe içinde navigasyon oldu
 *   - "error" — auth/network hatası — parent logout/retry yapabilir
 */

const TOKEN_STORAGE_KEY = 're_jwe_token';
const PARENT_ORIGIN = import.meta.env.VITE_PARENT_ORIGIN || '*';

export function getStoredToken() {
  // Sadece in-memory (postMessage ile gelen) veya sessionStorage
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token) {
  if (token) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

// ---------------------------------------------------------------------------
// On-demand token refresh — apiClient 401 aldığında parent'tan güncel token
// ister (kök neden: export anında token sessionStorage'da olmayabiliyordu).
// requestTokenFromParent() bir 'auth:request' postlar ve parent 'auth:token'
// ile cevap verene kadar (veya timeout'a kadar) bekler.
// ---------------------------------------------------------------------------
const _pendingTokenWaiters = [];

function _resolvePendingTokenWaiters(token) {
  while (_pendingTokenWaiters.length) {
    const w = _pendingTokenWaiters.shift();
    if (w.timer) clearTimeout(w.timer);
    w.resolve(token || null);
  }
}

/**
 * Parent spine app'ten güncel token iste ve gelene kadar bekle.
 *
 * 401 retry akışında kullanılır: token sessionStorage'da yoksa veya geçersizse
 * parent'a "auth:request" postlanır; parent güncel token'ı "auth:token" ile
 * geri gönderince Promise resolve olur. timeoutMs içinde token gelmezse reject.
 *
 * @param {number} timeoutMs - token gelmezse reddetme süresi (vars. 3000ms)
 * @returns {Promise<string|null>} yeni token (veya timeout'ta reject)
 */
export function requestTokenFromParent(timeoutMs = 3000) {
  // iframe içinde değilsek parent yok — refresh imkânsız.
  if (typeof window === 'undefined' || window.parent === window) {
    return Promise.reject(new Error('no-parent'));
  }
  return new Promise((resolve, reject) => {
    const waiter = { resolve, reject, timer: null };
    waiter.timer = setTimeout(() => {
      const idx = _pendingTokenWaiters.indexOf(waiter);
      if (idx !== -1) _pendingTokenWaiters.splice(idx, 1);
      reject(new Error('token-refresh-timeout'));
    }, timeoutMs);
    _pendingTokenWaiters.push(waiter);
    // Parent'a "güncel token'ı yeniden gönder" iste.
    postToParent('auth:request', {});
  });
}

/**
 * iframe parent'a mesaj gönder.
 * Production'da VITE_PARENT_ORIGIN ile hedef origin belirlenmeli.
 */
export function postToParent(type, payload = {}) {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage({ source: 'rule-engine', type, payload }, PARENT_ORIGIN);
  } catch (err) {
    // Cross-origin restriction — güvenli fallback
    console.warn('[RuleEngine iframe] postToParent failed:', err.message);
  }
}

/**
 * Parent'tan gelen mesajları dinle.
 * - auth:token → token'ı sakla
 * - auth:logout → token'ı temizle ve parent'a ready bildir
 * - navigate → router'ı yönlendir (custom event)
 */
export function initIframeBridge() {
  if (typeof window === 'undefined') return;

  // Parent origin kontrolü — güvenlik için sadece allowlist'teki origin'den al
  const allowedOrigins = (import.meta.env.VITE_PARENT_ORIGIN || '').split(',').map((s) => s.trim()).filter(Boolean);

  window.addEventListener('message', (event) => {
    // Güvenlik kontrolü — production'da kesinlikle açık olmalı
    if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin) && event.origin !== window.location.origin) {
      return;
    }

    const msg = event.data;
    if (!msg || typeof msg !== 'object' || msg.source === 'rule-engine') return;

    switch (msg.type) {
      case 'auth:token':
        setStoredToken(msg.payload?.token);
        // Bekleyen 401-retry isteklerini güncel token ile çöz.
        _resolvePendingTokenWaiters(msg.payload?.token);
        // Store'da da güncelle
        window.dispatchEvent(new CustomEvent('re:auth:token', { detail: msg.payload }));
        break;

      case 'auth:logout':
        setStoredToken(null);
        window.dispatchEvent(new CustomEvent('re:auth:logout'));
        break;

      case 'navigate':
        if (msg.payload?.path) {
          window.dispatchEvent(new CustomEvent('re:navigate', { detail: msg.payload }));
        }
        break;

      case 'theme':
        // Gelecekte dark mode vb. — şimdilik noop
        break;

      default:
        // Bilinmeyen mesaj — sessizce yoksay
        break;
    }
  });

  // iframe hazır — parent token push edebilir
  postToParent('ready', { version: '0.1.0' });

  // Auto-resize: içerik değişince parent'a yüksekliği bildir
  if (window.ResizeObserver) {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        postToParent('height', { height: entry.target.scrollHeight });
      }
    });
    // root element yüklendiğinde observe et
    setTimeout(() => {
      const root = document.getElementById('root');
      if (root) ro.observe(root);
    }, 0);
  }
}

/**
 * Uygulamanın iframe içinde (spine_app gibi bir parent altında) çalışıp
 * çalışmadığını belirle.
 *
 * - `window.self !== window.top` → kesin iframe.
 * - `?embedded=1` query param'ı → dev/QA için standalone URL'de embedded
 *   görünümü test etmek için override (override URL'den temizlenmez çünkü
 *   alt sayfalara navigasyonda da geçerli kalmalı).
 *
 * Değer bir kez hesaplanır ve module-scope'ta cache'lenir — sayfa içi
 * navigasyonda değişmez.
 */
let _embeddedCache = null;
export function isEmbedded() {
  if (_embeddedCache !== null) return _embeddedCache;
  if (typeof window === 'undefined') {
    _embeddedCache = false;
    return false;
  }
  try {
    if (window.self !== window.top) {
      _embeddedCache = true;
      return true;
    }
  } catch {
    // Cross-origin erişim hatası — iframe içinde olduğumuzu gösterir
    _embeddedCache = true;
    return true;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') {
      _embeddedCache = true;
      return true;
    }
  } catch {
    // ignore
  }
  _embeddedCache = false;
  return false;
}

/**
 * DEV için — URL query param olarak token gelebilir (?token=xxx)
 * Production'da kesinlikle kullanılmaz.
 */
export function readTokenFromQuery() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    setStoredToken(token);
    // URL'den temizle (query string görünmesin)
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.toString());
  }
  return token;
}
